import React, { useState, useEffect } from "react";
import SearchTable from "./search";
import Pagenation from "./pagenation";
import EditOrders from "./editOrders";
import { CircularProgress, Box } from "@mui/material";

export default function RenderTableOrders({
  paginatedOrders,
  allOrders,
  onSearch,
  onAggregatorChange,
  searchValue,
  aggregatorValue,
  currentPage,
  totalPages,
  availableAggregators = [],
  handlePageChange,
  onEdit,
  onDelete,
  userData,
  isLoading,
  totalItems = 0,
  itemsPerPage = 10,
}) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const isAdmin = userData?.isAdmin === true;
  const [searchInput, setSearchInput] = useState(searchValue || "");
  const [selectedAggregator, setSelectedAggregator] = useState(
    aggregatorValue || ""
  );

  // Sync props -> local state when parent resets (e.g., clearing search)
  useEffect(() => setSearchInput(searchValue || ""), [searchValue]);
  useEffect(
    () => setSelectedAggregator(aggregatorValue || ""),
    [aggregatorValue]
  );

  // Debounce search input before calling parent onSearch
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== (searchValue || "")) {
        onSearch(searchInput);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleViewDetails = (billNo) => {
    setExpandedRow(expandedRow === billNo ? null : billNo);
  };

  const handlePrintDetails = async (order) => {
    try {
      console.log("🟢 Printing Started", order);

      const response = await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/printkot`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: order._id }), // ✅ send only ID
        }
      );

      const data = await response.json();
      if (response.ok) {
        alert("✅ KOT Printed!");
      } else {
        alert("❌ Print Failed: " + data.error);
      }
    } catch (err) {
      console.error(err);
      alert("❌ Print Error occurred");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
  };

  const handleCloseEdit = () => {
    setEditingOrder(null);
  };

  // const aggregators = [
  //   ...new Set(allOrders.map((order) => order.aggregator)),
  // ].filter(Boolean);

  const aggregators = availableAggregators.length > 0 
    ? availableAggregators 
    : [...new Set(allOrders.map((order) => order.aggregator))].filter(Boolean); // Fallback

  if (editingOrder) {
    return (
      <div className="edit-order-wrapper">
        <EditOrders order={editingOrder} onClose={handleCloseEdit} />
      </div>
    );
  }

  return (
    <div className="table-container">
      <div
        className="table-header"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginBottom: "1.5rem",
          alignItems: "flex-start",
        }}
      >
        <SearchTable
          aggregators={aggregators}
          onSearch={(v) => setSearchInput(v)}
          onAggregatorChange={(v) => {
            setSelectedAggregator(v);
            onAggregatorChange(v);
          }}
          searchValue={searchInput}
          aggregatorValue={selectedAggregator}
        />
      </div>

      <div className="table-responsive-wrapper">
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Bill No</th>
                <th>Train</th>
                <th>Status</th>
                <th>Total Amount</th>
                <th>Sub Total</th>
                <th>Payment Status</th>
                <th>Aggregator</th>
                <th className="action-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    No Orders found
                  </td>
                </tr>
              ) : (
                (() => {
                  // ✅ Sort by Bill No (Descending)
                  const sortedOrders = [...paginatedOrders].sort((a, b) => {
                    const billA = parseInt(a.billNo || 0);
                    const billB = parseInt(b.billNo || 0);
                    return billB - billA;
                  });

                  return sortedOrders.map((order) => (
                    <React.Fragment key={order._id || order.id || order.billNo}>
                      <tr
                        className="order-row"
                        data-order-id={order._id || order.id || order.billNo}
                      >
                        <td>{order.billNo || "N/A"}</td>
                        <td>{order.trainName || "N/A"}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              order.status
                                ? String(order.status)
                                    .toLowerCase()
                                    .replace(/\s/g, "-")
                                : "pending"
                            }`}
                          >
                            {order.status || "N/A"}
                          </span>
                        </td>
                        <td>₹{order.totalAmount}</td>
                        <td>₹{order.subTotal}</td>
                        <td>
                          <span
                            className={`payment-status-badge ${
                              order.paymentStatus
                                ? String(order.paymentStatus).toLowerCase()
                                : "unpaid"
                            }`}
                          >
                            {order.paymentStatus || "-"}
                          </span>
                        </td>
                        <td>{order.aggregator || "N/A"}</td>
                        <td className="action-cell p-3">
                          <button
                            className="btn btn-sm icon-only"
                            onClick={() => handleEdit(order)}
                            title="Edit Order"
                          >
                            <i className="fas fa-edit"></i>
                          </button>

                          {isAdmin && (
                            <button
                              className="btn btn-sm icon-only"
                              onClick={() => onDelete(order._id)}
                              title="Delete Order"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}

                          <button
                            className="btn btn-sm icon-only"
                            onClick={() => handlePrintDetails(order)}
                            title="Print Details"
                          >
                            <i className="fas fa-print"></i>
                          </button>

                          <button
                            className="btn btn-sm icon-only"
                            onClick={() =>
                              handleViewDetails(order._id || order.billNo)
                            }
                            title="View Details"
                          >
                            {expandedRow === (order._id || order.billNo) ? (
                              <i className="fas fa-eye-slash"></i>
                            ) : (
                              <i className="fas fa-eye"></i>
                            )}
                          </button>
                        </td>
                      </tr>

                      {expandedRow === (order._id || order.billNo) && (
                        <tr className="detail-row">
                          <td colSpan="12">
                            <div className="expanded-details-card d-flex flex-row gap-3 flex-wrap m-1">
                              <p>
                                <strong>Train:</strong>{" "}
                                {order.trainNo || "No Data"}
                              </p>
                              <p>
                                <strong>Reason:</strong>{" "}
                                {order.reason || "Successfully Delivered"}
                              </p>
                              <p>
                                <strong>Order ID:</strong>{" "}
                                {order.orderId || "N/A"}
                              </p>
                              <p>
                                <strong>Order Date:</strong>{" "}
                                {order.orderDate || "N/A"}
                              </p>
                              <p>
                                <strong>Customer:</strong>{" "}
                                {order.customerName || "N/A"}
                              </p>
                              {order.items && order.items.length > 0 && (
                                <div className="item-list-details m-2">
                                  <strong>Items:</strong>
                                  <ul className="d-flex flex-row gap-5 flex-wrap">
                                    <div>
                                      {order.items.map((item, index) => (
                                        <li
                                          key={index}
                                          style={{ fontSize: "0.9rem" }}
                                        >
                                          {item.qty}x {item.name} ₹
                                          {Number(item.price).toFixed(2)} each :
                                          <strong>
                                            ₹
                                            {Number(
                                              item.amount ||
                                                item.qty * item.price
                                            ).toFixed(2)}
                                          </strong>
                                        </li>
                                      ))}
                                      <li className="subtotal">
                                        <strong>SubTotal: </strong>₹
                                        {order.subTotal}
                                      </li>
                                    </div>
                                    <div>
                                      <li className="gst">
                                        <strong>GST: </strong>₹{order.gst}
                                      </li>
                                      <li className="discount">
                                        <strong>Discount: </strong>₹
                                        {order.discount || 0}
                                      </li>
                                      <li className="delivery">
                                        <strong>Delivery: </strong>₹
                                        {order.deliveryCharge || 0}
                                      </li>
                                      <li className="gatewayfee">
                                        <strong>GateWay Fee: </strong>₹
                                        {order.gatewayFee || 0}
                                      </li>
                                      <li className="total-amount">
                                        <strong>Total Amount: </strong>₹
                                        {order.totalAmount}
                                      </li>
                                    </div>
                                  </ul>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ));
                })()
              )}
            </tbody>
          </table>
        )}

        {/* Summary Section */}
        {/* <div className="table-summary d-flex flex-row gap-4 flex-wrap mt-3">
          <h6>Today Orders: {allOrders.length}</h6>
          <h6>
            SubTotal Revenue: ₹
            {allOrders
              .filter((order) => order.status?.toLowerCase() !== "cancelled")
              .reduce(
                (acc, order) => acc + (parseFloat(order.subTotal) || 0),
                0
              )}
          </h6>
          <h6>
            Today Revenue: ₹
            {allOrders
              .filter((order) => order.status?.toLowerCase() !== "cancelled")
              .reduce(
                (acc, order) => acc + (parseFloat(order.totalAmount) || 0),
                0
              )}
          </h6>
          <h6>
            Cancelled Orders: ₹
            {allOrders
              .filter((order) => order.status?.toLowerCase() === "cancelled")
              .reduce(
                (acc, order) => acc + (parseFloat(order.totalAmount) || 0),
                0
              )}
          </h6>
        </div> */}
      </div>

      {!isLoading && (
        <Pagenation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={totalItems || allOrders.length}
          itemsPerPage={itemsPerPage || paginatedOrders.length || 10}
        />
      )}
    </div>
  );
}
